import { ConfirmationNumber } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Batch from '../api/batch';
import CrapBulkToken from '../api/crapBulkToken';
import { show } from '../reducers/notificationSlice';

const SCOPE_OPTIONS = {
    department: 'department',
    specialisation: 'specialisation',
    batch: 'batch',
};

const INITIAL_FORM = {
    scopeType: SCOPE_OPTIONS.department,
    departmentID: '',
    specialisationID: '',
    batchID: '',
};

function formatBatchLabel(batch) {
    if (!batch) return '';
    const startYear = String(batch?.startYear || '').trim();
    const endYear = String(batch?.endYear || '').trim();
    const yearLabel = startYear && endYear ? `${startYear}-${endYear}` : startYear || endYear || '';
    const sectionLabel = batch?.section ? `Section ${batch.section}` : '';
    const excelLabel = batch?.actualExcelFileName ? batch.actualExcelFileName : '';
    return [yearLabel, sectionLabel, excelLabel].filter(Boolean).join(' • ') || String(batch?.id || '');
}

function buildCreatePayload(formData, institutionID) {
    const payload = {
        institutionID,
        departmentID: formData.departmentID,
    };

    if (formData.specialisationID) payload.specialisationID = formData.specialisationID;
    if (formData.batchID) payload.batchID = formData.batchID;
    return payload;
}

function getScopeLabel(formData) {
    if (formData.scopeType === SCOPE_OPTIONS.batch) return 'Department + specialisation + batch';
    if (formData.scopeType === SCOPE_OPTIONS.specialisation) return 'Department + specialisation';
    if (formData.departmentID) return 'Entire department';
    return 'Select a scope';
}

export default function CrapBulkTokenManager({ institutionID, institutionName, open, onClose }) {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [departments, setDepartments] = useState([]);
    const [specialisations, setSpecialisations] = useState([]);
    const [batches, setBatches] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [loadingSpecialisations, setLoadingSpecialisations] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [createdToken, setCreatedToken] = useState(null);

    useEffect(() => {
        if (!open) {
            setFormData(INITIAL_FORM);
            setDepartments([]);
            setSpecialisations([]);
            setBatches([]);
            setCreatedToken(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !institutionID) {
            setDepartments([]);
            setSpecialisations([]);
            setBatches([]);
            return;
        }

        let active = true;
        setLoadingDepartments(true);
        setDepartments([]);
        setSpecialisations([]);
        setBatches([]);

        Batch.getDepartments(institutionID)
            .then(({ data }) => {
                if (!active) return;
                setDepartments(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (!active) return;
                dispatch(
                    show({
                        message: err?.response?.data?.message || 'Failed to load departments',
                        open: true,
                        severity: 'error',
                    })
                );
            })
            .finally(() => {
                if (active) setLoadingDepartments(false);
            });

        return () => {
            active = false;
        };
    }, [dispatch, institutionID, open]);

    useEffect(() => {
        if (!open || !institutionID || !formData.departmentID) {
            setSpecialisations([]);
            setBatches([]);
            return;
        }

        let active = true;
        setLoadingSpecialisations(true);
        setSpecialisations([]);
        setBatches([]);

        Batch.getSpecialisations(institutionID, formData.departmentID)
            .then(({ data }) => {
                if (!active) return;
                setSpecialisations(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (!active) return;
                dispatch(
                    show({
                        message: err?.response?.data?.message || 'Failed to load specialisations',
                        open: true,
                        severity: 'error',
                    })
                );
            })
            .finally(() => {
                if (active) setLoadingSpecialisations(false);
            });

        return () => {
            active = false;
        };
    }, [dispatch, formData.departmentID, institutionID, open]);

    useEffect(() => {
        if (!open || !institutionID || !formData.departmentID || !formData.specialisationID) {
            setBatches([]);
            return;
        }

        let active = true;
        setLoadingBatches(true);
        setBatches([]);

        (async () => {
            try {
                let lastEvaluatedKey = null;
                const collected = [];

                do {
                    // Batch API caps page size at 10, so keep walking until all matching batches are loaded.
                    // eslint-disable-next-line no-await-in-loop
                    const { data } = await Batch.listBatches(institutionID, {
                        departmentID: formData.departmentID,
                        specialisationID: formData.specialisationID,
                        limit: 10,
                        lastEvaluatedKey,
                    });

                    const items = Array.isArray(data?.batches) ? data.batches : [];
                    collected.push(...items);
                    lastEvaluatedKey = data?.lastEvaluatedKey || null;
                } while (lastEvaluatedKey);

                if (!active) return;
                const deduped = Array.from(new Map(collected.map((item) => [String(item?.id || ''), item])).values());
                setBatches(deduped);
            } catch (err) {
                if (!active) return;
                dispatch(
                    show({
                        message: err?.response?.data?.message || 'Failed to load batches',
                        open: true,
                        severity: 'error',
                    })
                );
            } finally {
                if (active) setLoadingBatches(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [dispatch, formData.departmentID, formData.specialisationID, institutionID, open]);

    const canCreateToken =
        Boolean(
            institutionID &&
            formData.departmentID &&
            (
                formData.scopeType === SCOPE_OPTIONS.department ||
                (formData.scopeType === SCOPE_OPTIONS.specialisation && formData.specialisationID) ||
                (formData.scopeType === SCOPE_OPTIONS.batch && formData.specialisationID && formData.batchID)
            )
        ) &&
        !submitting;

    const handleFieldChange = (field) => (event) => {
        const value = event.target.value;
        setCreatedToken(null);
        setFormData((prev) => {
            if (field === 'departmentID') {
                return {
                    ...INITIAL_FORM,
                    departmentID: value,
                };
            }
            if (field === 'specialisationID') {
                return {
                    ...prev,
                    specialisationID: value,
                    batchID: '',
                };
            }
            return {
                ...prev,
                [field]: value,
            };
        });
    };

    const handleScopeTypeChange = (_event, nextScopeType) => {
        if (!nextScopeType) return;
        setCreatedToken(null);
        setFormData((prev) => ({
            ...prev,
            scopeType: nextScopeType,
            specialisationID:
                nextScopeType === SCOPE_OPTIONS.department ? '' : prev.specialisationID,
            batchID: nextScopeType === SCOPE_OPTIONS.batch ? prev.batchID : '',
        }));
    };

    const handleSubmit = async () => {
        if (!canCreateToken) return;
        setSubmitting(true);

        try {
            const payload = buildCreatePayload(formData, institutionID);
            const { data } = await CrapBulkToken.createToken(payload);
            setCreatedToken(data);
            dispatch(
                show({
                    message: 'Bulk token created successfully',
                    open: true,
                    severity: 'success',
                })
            );
        } catch (err) {
            dispatch(
                show({
                    message: err?.response?.data?.message || 'Failed to create bulk token',
                    open: true,
                    severity: 'error',
                })
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ConfirmationNumber color="primary" />
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Create C-RAP Bulk Token
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {institutionName || 'Selected institution'}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Alert severity="info">
                        Create a token for the full department, for one department + specialisation, or for one
                        department + specialisation + batch.
                    </Alert>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Token scope
                        </Typography>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={formData.scopeType}
                            onChange={handleScopeTypeChange}
                            size="small"
                            sx={{ flexWrap: 'wrap', gap: 1 }}
                        >
                            <ToggleButton value={SCOPE_OPTIONS.department}>
                                Entire department
                            </ToggleButton>
                            <ToggleButton value={SCOPE_OPTIONS.specialisation}>
                                Department + specialisation
                            </ToggleButton>
                            <ToggleButton value={SCOPE_OPTIONS.batch}>
                                Department + specialisation + batch
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <TextField
                        fullWidth
                        label="Institution"
                        value={institutionName || institutionID || ''}
                        disabled
                    />

                    <TextField
                        select
                        fullWidth
                        label="Department"
                        value={formData.departmentID}
                        onChange={handleFieldChange('departmentID')}
                        disabled={!institutionID || loadingDepartments}
                        helperText={loadingDepartments ? 'Loading departments...' : 'Required'}
                    >
                        <MenuItem value="">Select department</MenuItem>
                        {departments.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        fullWidth
                        label="Specialisation"
                        value={formData.specialisationID}
                        onChange={handleFieldChange('specialisationID')}
                        disabled={
                            !formData.departmentID ||
                            loadingSpecialisations ||
                            formData.scopeType === SCOPE_OPTIONS.department
                        }
                        helperText={
                            formData.scopeType === SCOPE_OPTIONS.department
                                ? 'Not needed for department-wide token'
                                : !formData.departmentID
                                ? 'Select department first'
                                : loadingSpecialisations
                                    ? 'Loading specialisations...'
                                    : 'Required for this scope'
                        }
                    >
                        <MenuItem value="">Select specialisation</MenuItem>
                        {specialisations.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        fullWidth
                        label="Batch"
                        value={formData.batchID}
                        onChange={handleFieldChange('batchID')}
                        disabled={
                            !formData.specialisationID ||
                            loadingBatches ||
                            formData.scopeType !== SCOPE_OPTIONS.batch
                        }
                        helperText={
                            formData.scopeType !== SCOPE_OPTIONS.batch
                                ? 'Only needed for batch-level token'
                                : !formData.specialisationID
                                ? 'Batch-specific token requires a specialisation'
                                : loadingBatches
                                    ? 'Loading batches...'
                                    : 'Required for this scope'
                        }
                    >
                        <MenuItem value="">Select batch</MenuItem>
                        {batches.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {formatBatchLabel(item)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {formData.departmentID ? <Chip label="Department selected" color="primary" size="small" /> : null}
                        {formData.specialisationID ? (
                            <Chip label="Specialisation selected" color="primary" size="small" />
                        ) : formData.scopeType === SCOPE_OPTIONS.department && formData.departmentID ? (
                            <Chip label="Department-wide token" color="primary" size="small" />
                        ) : null}
                        {formData.batchID ? <Chip label="Batch selected" color="secondary" size="small" /> : null}
                        <Chip label={`Scope: ${getScopeLabel(formData)}`} size="small" variant="outlined" />
                    </Stack>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => onClose()} disabled={submitting} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!canCreateToken}
                            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ConfirmationNumber />}
                        >
                            {submitting ? 'Creating token...' : 'Create token'}
                        </Button>
                    </Box>

                    {createdToken?.token ? (
                <Alert severity="success" sx={{ alignItems: 'flex-start' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Token created
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Share this token once with the intended team. Only the encoded value is stored in the backend.
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            mt: 1.5,
                            fontFamily: 'monospace',
                            letterSpacing: 1.2,
                            wordBreak: 'break-all',
                        }}
                    >
                        {createdToken.token}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        {createdToken?.scope?.departmentID ? (
                            <Chip label={`Department: ${createdToken.scope.departmentID}`} size="small" />
                        ) : null}
                        {createdToken?.scope?.specialisationID ? (
                            <Chip label={`Specialisation: ${createdToken.scope.specialisationID}`} size="small" />
                        ) : null}
                        {createdToken?.scope?.batchID ? (
                            <Chip label={`Batch: ${createdToken.scope.batchID}`} size="small" />
                        ) : null}
                        {createdToken?.scope?.scopeType ? (
                            <Chip label={`Scope type: ${createdToken.scope.scopeType}`} size="small" />
                        ) : null}
                    </Stack>
                </Alert>
            ) : null}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
